import { Check, ChevronUp, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { remainingMs, secondsLeft } from "@/lib/qrAccept";
import { tableLabel, type AcceptPrompt } from "./qrAcceptPromptState";

interface QRAcceptPromptCardProps {
  prompt: AcceptPrompt;
  /** Device clock (ms) of this tick. */
  now: number;
  /** Server clock minus device clock (ms). */
  skewMs: number;
  /** qr_orders.manage: Review opens the QR orders panel. */
  canReview: boolean;
  onAccept: (prompt: AcceptPrompt) => void;
  onReview: () => void;
  onMinimize: () => void;
  onDismiss: (prompt: AcceptPrompt) => void;
}

/**
 * One QR order waiting for a taker: the table, what was ordered, how long is
 * left before it is auto-accepted, and Accept. Read at arm's length on a
 * counter tablet: the table number is the biggest thing on it.
 */
export function QRAcceptPromptCard({
  prompt,
  now,
  skewMs,
  canReview,
  onAccept,
  onReview,
  onMinimize,
  onDismiss,
}: QRAcceptPromptCardProps) {
  const left = remainingMs(prompt.deadlineAt, now, skewMs);
  const secs = secondsLeft(left);
  const fraction = prompt.windowMs > 0 ? Math.min(1, left / prompt.windowMs) : 0;
  const label = tableLabel(prompt);
  const resolved = prompt.phase === "resolved";
  const attention = prompt.phase === "attention";
  const accepting = prompt.phase === "accepting";
  const counting = !resolved && !attention;

  return (
    <section
      aria-label={`QR order, ${label}`}
      data-testid="qr-accept-prompt"
      data-phase={prompt.phase}
      className="pointer-events-auto overflow-hidden rounded-2xl border border-line bg-card text-ink elev-1 animate-in fade-in-0 slide-in-from-top-2 duration-200"
    >
      {counting && (
        <div
          role="progressbar"
          aria-label="Time left to accept"
          aria-valuemin={0}
          aria-valuemax={Math.round(prompt.windowMs / 1000)}
          aria-valuenow={secs}
          className="h-1 w-full bg-muted"
        >
          <div
            className="h-full origin-left bg-primary"
            style={{ transform: `scaleX(${fraction})` }}
          />
        </div>
      )}

      <div className="p-4">
        <div className="flex items-center justify-between gap-2">
          <p className="m-0 text-eyebrow uppercase text-ink-3">{attention ? "Needs attention" : "New QR order"}</p>
          {attention ? (
            <Button
              variant="ghost"
              size="icon"
              className="-mr-2 -mt-1 h-8 w-8 text-ink-3"
              aria-label={`Dismiss ${label}`}
              onClick={() => onDismiss(prompt)}
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="-mr-2 -mt-1 h-8 w-8 text-ink-3"
              aria-label="Minimize QR orders"
              onClick={onMinimize}
            >
              <ChevronUp aria-hidden="true" className="h-4 w-4" />
            </Button>
          )}
        </div>

        <div className="mt-1 flex items-baseline justify-between gap-3">
          <h2 className="m-0 truncate text-[30px] font-semibold leading-9 tracking-tight tabular">{label}</h2>
          {counting && (
            <span
              data-testid="qr-accept-countdown"
              className={cn(
                "shrink-0 font-mono tabular",
                secs > 0 ? "text-metric text-ink" : "text-body font-medium text-ink-2",
              )}
            >
              {secs > 0 ? `${secs}s` : "Auto-accepting…"}
            </span>
          )}
        </div>

        {resolved ? (
          // Announced once, by the prompts' live region (no role="status" here).
          <p className="m-0 mt-3 flex items-center gap-2 text-emphasis text-ink-2">
            <Check aria-hidden="true" className="h-4 w-4 shrink-0" />
            {prompt.outcome}
          </p>
        ) : (
          <>
            <ul className="m-0 mt-2 list-none space-y-0.5 p-0 text-[15px] leading-[22px]">
              {prompt.lines.map((line, i) => (
                <li key={i} className="truncate">
                  <span className="tabular font-semibold">{line.quantity} ×</span> {line.name}
                  {line.variant && <span className="text-ink-2"> · {line.variant}</span>}
                </li>
              ))}
              {prompt.more > 0 && <li className="text-ink-3">+{prompt.more} more</li>}
            </ul>

            {attention && prompt.reason && (
              <p className="m-0 mt-2 text-meta text-[hsl(var(--status-billing-ink))]">
                Couldn't auto-accept: {prompt.reason}
              </p>
            )}
            {prompt.error && (
              <p role="alert" className="m-0 mt-2 text-meta text-destructive">
                {prompt.error}
              </p>
            )}

            <div className="mt-3 flex gap-2">
              <Button
                className="h-12 flex-1 text-base font-semibold"
                onClick={() => onAccept(prompt)}
                disabled={accepting}
                aria-label={`Accept ${label}`}
                data-qr-accept
              >
                {accepting && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}
                {accepting ? "Accepting…" : "Accept"}
              </Button>
              {canReview && (
                <Button variant="outline" className="h-12 px-4 text-base" onClick={onReview} disabled={accepting}>
                  Review
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
